import { useAxiosStore } from '@risksmart-app/components/src/hooks/useAxios';
import { useCallback } from 'react';
import { useTRPCClient } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Fetches a CSV import template as a Blob, ready to hand to `downloadBlob`.
 *
 * A flag-gated V2/V3 twin, hand-written rather than built with `createQueryHook`
 * (COI-704): that factory pairs a tRPC query with an Apollo GraphQL twin, and
 * the V2 leg here is a direct rest-api call — `GET /data-import/template` was
 * never a Hasura operation. It is also an on-demand fetch (a click), not a
 * render-time query, so it returns a callback rather than query state.
 *
 * The two legs differ only in transport: V2 responds `text/csv` and axios gives
 * back a Blob directly; V3 carries the same bytes as a JSON string (the
 * data-layer read surface is JSON-only), so the Blob is built here. The template
 * body itself comes from one shared module on both paths, so the downloaded file
 * is identical either way.
 */
export const useGetDataImportTemplate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpcClient = useTRPCClient();
  const { authorisedAxiosInstance } = useAxiosStore();

  return useCallback(
    async (csvFile: string): Promise<Blob> => {
      if (trpcEnabled) {
        const { Csv } = await trpcClient.frontend.dataImport.template.query({
          type: csvFile,
        });

        return new Blob([Csv], { type: 'text/csv' });
      }

      const result = await authorisedAxiosInstance.get<Blob>(
        '/data-import/template',
        {
          params: { type: csvFile },
          responseType: 'blob',
        }
      );

      return result.data;
    },
    [trpcEnabled, trpcClient, authorisedAxiosInstance]
  );
};
